import { useRef, useState } from 'react'
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { Alert, AlertTitle, Stack } from '@mui/material'
import { useGetDocumentApprovalState } from '@/service-library/hooks/document-approvals'
import { Document } from '@/types/documents'
import ImageZoomPan from '@/components/image-zoom-pan/ImageZoomPan'
import ImageZoomPanControls from '@/components/image-zoom-pan/ImageZoomPanControls'
import NavigationPanel from '@/components/image-zoom-pan/NavigationPanel'
import DocumentImagePageProvider from '@/components/image-zoom-pan/providers/DocumentImagePageProvider'
import ImageZoomPanCenterProvider from '@/components/image-zoom-pan/providers/ImageZoomPanCenterProvider'
import ValidationOptionsProvider from '@/components/image-zoom-pan/providers/ValidationOptionsProvider'
import SizeProvider from '@/components/size-provider/SizeProvider'
import ValidationFieldsPanel from '@/components/validation/ValidationFieldsPanel'
import ApprovalsNeededCard from './ApprovalsNeededCard'
import DocumentApprovalSummaryCard from './DocumentApprovalSummaryCard'

type DocumentApprovalsViewProps = {
  document: Document
}

export default function DocumentApprovalsView({
  document,
}: DocumentApprovalsViewProps) {
  const transformerRef = useRef<ReactZoomPanPinchRef>(null)
  const [drawBoxes, setDrawBoxes] = useState(true)
  const [rotationDegree, setRotationDegree] = useState(0)
  // Handing the panel's container to the viewer is what centers the image in
  // the space left of the cards instead of the full viewport width.
  const [fieldsContainerEl, setFieldsContainerEl] =
    useState<HTMLDivElement | null>(null)

  const rotateImage = () => {
    setRotationDegree((prev) => (prev + 90 === 360 ? 0 : prev + 90))
  }

  const { approvalState } = useGetDocumentApprovalState({ id: document.id })

  const showSatisfiedBanner =
    !approvalState?.is_rejected &&
    !!approvalState?.all_policies_satisfied &&
    (approvalState?.policies.length ?? 0) > 0

  return (
    // Same floating-panel arrangement as the validation screen. Editing is
    // already disabled at the DocumentProvider level, not here.
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        position: 'relative',
        minHeight: 0,
        minWidth: 0,
        flexGrow: 1,
        overflow: 'hidden',
      }}
    >
      <ValidationOptionsProvider
        document={document}
        localStorageKeyPrefix="approvals"
      >
        <DocumentImagePageProvider document={document}>
          <ImageZoomPanCenterProvider transformerRef={transformerRef}>
            <SizeProvider>
              <ImageZoomPan
                key={document.id}
                drawBoxes={drawBoxes}
                rotationDegree={rotationDegree}
                transformerRef={transformerRef}
                layout="right"
                fieldsContainerEl={fieldsContainerEl}
              />
            </SizeProvider>
            <NavigationPanel
              controls={[
                <ImageZoomPanControls
                  documentView="approvals"
                  key="zoom-pan-controls"
                  hidePaginationControls={
                    !document.document_pages.length ||
                    document.document_pages.length <= 1
                  }
                  layout="right"
                  rotateImage={rotateImage}
                  setDrawBoxes={setDrawBoxes}
                />,
              ]}
              layout="right"
            />
            <ValidationFieldsPanel
              layout="right"
              storagePrefix="approvals"
              defaultWidth="30%"
              setFieldsContainerEl={setFieldsContainerEl}
            >
              {showSatisfiedBanner && (
                <Alert
                  severity="success"
                  icon={<CheckCircle fontSize="inherit" />}
                  sx={{ pointerEvents: 'all' }}
                >
                  <AlertTitle>All approvals complete</AlertTitle>
                  All approval policies applied to this document have been
                  satisfied.
                </Alert>
              )}

              <ApprovalsNeededCard documentId={document.id} />

              <DocumentApprovalSummaryCard />
            </ValidationFieldsPanel>
          </ImageZoomPanCenterProvider>
        </DocumentImagePageProvider>
      </ValidationOptionsProvider>
    </Stack>
  )
}
